import apiService from './api';

export interface ActivityArea {
  _id: string;
  id?: string;
  name: string;
  description?: string;
  icon?: string;
  isActive: boolean;
  hasPostalDelivery: boolean;
  isDeleted?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateActivityAreaDto {
  name: string;
  description?: string;
  icon?: string;
  isActive?: boolean;
  hasPostalDelivery?: boolean;
}

export interface UpdateActivityAreaDto {
  name?: string;
  description?: string;
  icon?: string;
  isActive?: boolean;
  hasPostalDelivery?: boolean;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

class ActivityAreaService {
  private baseUrl = '/admin/activity-areas';

  // دریافت همه حوزه‌های فعالیت با پیجینیشن
  public async getActivityAreas(
    page: number = 1,
    limit: number = 10,
    search?: string,
    isActive?: boolean,
    hasPostalDelivery?: boolean
  ): Promise<PaginatedResponse<ActivityArea>> {
    try {
      const params: Record<string, any> = { page, limit };
      if (search) params.search = search;
      if (isActive !== undefined) params.isActive = isActive;
      if (hasPostalDelivery !== undefined) params.hasPostalDelivery = hasPostalDelivery;

      // apiService.get برای این ساختار response، مستقیماً آرایه data را برمی‌گرداند
      // بنابراین باید از axios مستقیم استفاده کنیم یا ساختار را تطبیق دهیم
      const response = await apiService.get<any>(this.baseUrl, { params });
      
      console.log('Activity areas raw response:', response);

      // اگر response یک آرایه است (apiService.get بخش data را استخراج کرده)
      if (Array.isArray(response)) {
        return {
          data: response.map((item: any) => ({ ...item, id: item._id })),
          pagination: {
            total: response.length,
            page: page,
            limit: limit,
            totalPages: Math.ceil(response.length / limit)
          }
        };
      }

      // اگر response شامل data و pagination است
      if (response && response.data && response.pagination) {
        return {
          data: response.data.map((item: any) => ({ ...item, id: item._id })),
          pagination: response.pagination
        };
      }

      // اگر response فقط data دارد (و pagination جدا است)
      if (response && Array.isArray(response.data)) {
        return {
          data: response.data.map((item: any) => ({ ...item, id: item._id })),
          pagination: response.pagination || {
            total: response.data.length,
            page: page,
            limit: limit,
            totalPages: Math.ceil(response.data.length / limit)
          }
        };
      }

      // Fallback
      return {
        data: [],
        pagination: { total: 0, page: 1, limit: 10, totalPages: 0 }
      };
    } catch (error) {
      console.error('Error fetching activity areas:', error);
      throw error;
    }
  }

  // دریافت حوزه فعالیت با شناسه
  public async getActivityArea(id: string): Promise<ActivityArea> {
    try {
      const response = await apiService.get<any>(`${this.baseUrl}/${id}`);
      // apiService.get ممکن است مستقیماً data را برگرداند
      const activityArea = response._id ? response : response.data || response;
      return { ...activityArea, id: activityArea._id };
    } catch (error) {
      console.error(`Error fetching activity area with ID ${id}:`, error);
      throw error;
    }
  }

  // ایجاد حوزه فعالیت جدید
  public async createActivityArea(data: CreateActivityAreaDto): Promise<ActivityArea> {
    try {
      const response = await apiService.post<any>(this.baseUrl, data);
      const activityArea = response._id ? response : response.data || response;
      return { ...activityArea, id: activityArea._id };
    } catch (error) {
      console.error('Error creating activity area:', error);
      throw error;
    }
  }

  // به‌روزرسانی حوزه فعالیت
  public async updateActivityArea(id: string, data: UpdateActivityAreaDto): Promise<ActivityArea> {
    try {
      const response = await apiService.put<any>(`${this.baseUrl}/${id}`, data);
      const activityArea = response._id ? response : response.data || response;
      return { ...activityArea, id: activityArea._id };
    } catch (error) {
      console.error(`Error updating activity area with ID ${id}:`, error);
      throw error;
    }
  }

  // حذف حوزه فعالیت
  public async deleteActivityArea(id: string): Promise<ActivityArea> {
    try {
      const response = await apiService.delete<any>(`${this.baseUrl}/${id}`);
      const activityArea = response._id ? response : response.data || response;
      return { ...activityArea, id: activityArea._id };
    } catch (error) {
      console.error(`Error deleting activity area with ID ${id}:`, error);
      throw error;
    }
  }

  // بازیابی حوزه فعالیت حذف شده
  public async restoreActivityArea(id: string): Promise<ActivityArea> {
    try {
      const response = await apiService.post<any>(`${this.baseUrl}/${id}/restore`);
      const activityArea = response._id ? response : response.data || response;
      return { ...activityArea, id: activityArea._id };
    } catch (error) {
      console.error(`Error restoring activity area with ID ${id}:`, error);
      throw error;
    }
  }

  // تغییر وضعیت حوزه فعالیت
  public async toggleActivityAreaStatus(id: string): Promise<ActivityArea> {
    try {
      const response = await apiService.patch<any>(`${this.baseUrl}/${id}/status`);
      const activityArea = response._id ? response : response.data || response;
      return { ...activityArea, id: activityArea._id };
    } catch (error) {
      console.error(`Error toggling status for activity area with ID ${id}:`, error);
      throw error;
    }
  }

  // دریافت حوزه‌های فعالیت فعال (برای dropdown)
  public async getActiveActivityAreas(): Promise<ActivityArea[]> {
    try {
      const response = await apiService.get<any>(`${this.baseUrl}/active`);
      // apiService.get ممکن است مستقیماً آرایه data را برگرداند
      const items = Array.isArray(response) ? response : (response.data || []);
      return items.map((item: any) => ({ ...item, id: item._id }));
    } catch (error) {
      console.error('Error fetching active activity areas:', error);
      throw error;
    }
  }
}

export default new ActivityAreaService();

